import React, { Fragment, PropsWithChildren, useState } from 'react';
import {
  Grid,
  Table,
  TableColumnResizing,
  TableHeaderRow,
} from '@devexpress/dx-react-grid-bootstrap4';

import Tabs from '../../components/Tabs';
import PoweredByHeader from 'components/PoweredByHeader';
import SelectionSetSelector from 'components/uploads/form/SelectionSetSelector';
import PropertyDefinitionSelector from 'components/uploads/form/PropertyDefinitionSelector';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import { useGetQuery } from 'api/useGetQuery';
import { SelectionSet } from 'types/SelectionSet';
import { ENDPOINTS } from 'api/endpoints';
import { ValueType } from 'react-select';
import { usePostMutation } from 'api/usePostMutation';
import { Asset } from 'types/Asset';
import { OptionsType } from 'types/OptionsType';
import { PropertyDefinition } from 'types/PropertyDefinition';
import { UnmappedUserEnteredProductsResult } from 'types/UnmappedUserEnteredProductsResult';
import { LoadingIndicator } from 'components/LoadingIndicator';
import LookupCell from './LookupCell';
import {
  DataTypeProvider,
  DataTypeProviderProps,
  IntegratedSorting,
  SortingState,
} from '@devexpress/dx-react-grid';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';

import { showError } from 'utils/showError';
import { showSuccess } from 'utils/showSuccess';
import AssetLookup from '../../components/AssetLookup/AssetLookup';
import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';
import { useLocation, useHistory } from 'react-router-dom';
import { Button } from 'components/Button';
import RouteGuard from 'components/RouteGuard/RouteGuard';
import { routes } from 'routes';
import { errorMessage } from 'utils/errorMessage';
import UploadModal from './UploadModal';

type AssetForTable = {
  resourceId: number | null | undefined;
  name: string | null | undefined;
  property: string | null | undefined;
  productName: OptionsType | null | undefined;
  firmName: string | null | undefined;
  productId: number | null | undefined;
};

const sortProductName = (a?: OptionsType, b?: OptionsType) => {
  if (!a) return 1;
  if (!b) return -1;

  if (a?.label === b?.label) {
    return 0;
  }
  return a?.label < b?.label ? -1 : 1;
};

const AssetMappings: React.FC = () => {
  const [selectionSet, setSelectionSet] = useState<
    ValueType<OptionsType, false> | undefined | null
  >();
  const [isLoadingSelectionSet, setIsLoadingSelectionSet] = useState<boolean>(false);
  const [propertyDefinition, setPropertyDefinition] = useState<
    ValueType<OptionsType, false> | undefined | null
  >();
  const [isLoadingPropertyDefinition, setIsLoadingPropertyDefinition] = useState<boolean>(false);
  const [assets, setAssets] = useState<AssetForTable[]>([]);
  const [unmappedUserEnteredProducts, setUnmappedUserEnteredProducts] = useState<
    UnmappedUserEnteredProductsResult[] | null
  >(null);

  const [showConfirmAssetRemoval, setShowConfirmRemoval] = useState<boolean>(false);
  const [resourceIdToRemove, setResourceIdToRemove] = useState<number | null>();
  const [showConfirmChangeSelectionSet, setShowConfirmChangeSelectionSet] = useState<boolean>(
    false,
  );
  const [showConfirmClear, setShowConfirmClear] = useState<boolean>(false);
  const [selectionSetParams, setSelectionSetParams] = useState<{
    newSelectionSet: ValueType<OptionsType, false>;
    triggeredAction: any;
  } | null>();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const [dataEnvironment] = useDataEnvironment();
  const { isFetching: isFetchingSelectionSets, data: selectionSets } = useGetQuery<SelectionSet[]>(
    ENDPOINTS.assetSelectionSets(dataEnvironment!.id),
  );
  const { isFetching: isFetchingPropertyDefinitions, data: propertyDefinitions } = useGetQuery<
    PropertyDefinition[]
  >(ENDPOINTS.propertyDefinitions(dataEnvironment!.id));

  const history = useHistory();
  const location = useLocation();
  const [showAssetLookup, setShowAssetLookup] = useState<boolean>(false);
  const [hideNoDataMessage, setHideNoDataMessage] = useState<boolean>(false);

  const columns = [
    { name: 'name', title: 'Solovis Asset' },
    {
      name: 'property',
      title: propertyDefinition ? `Property (${propertyDefinition!.label})` : 'Property',
    },
    { name: 'productName', title: 'Product Name' },
    { name: 'firmName', title: 'Firm Name' },
    { name: 'productId', title: 'Product ID' },
    { name: 'actions', title: 'Actions' },
  ];

  const [defaultColumnWidths] = useState(
    columns.map((column) => ({
      columnName: column.name,
      width: (window.innerWidth - 50) / columns.length,
    })),
  );

  const mutate = usePostMutation<{
    assets: Asset[];
    propertyDefinitions: PropertyDefinition[];
  }>();

  const { data: mappedAssets } = useGetQuery<Asset[]>(ENDPOINTS.mappedAssets(dataEnvironment!.id));
  const mappedAssetProductUploadIds =
    mappedAssets?.map((asset) => asset.productUploadAssetId) || [];

  const {
    isLoading: isLoadingUnmappedUserData,
    data: unmappedUserEnteredProductsData,
  } = useGetQuery<UnmappedUserEnteredProductsResult[]>(
    ENDPOINTS.unmappedUserEnteredProducts(dataEnvironment!.id),
  );

  if (
    unmappedUserEnteredProducts !== unmappedUserEnteredProductsData &&
    !isLoadingUnmappedUserData &&
    unmappedUserEnteredProductsData
  ) {
    setUnmappedUserEnteredProducts(unmappedUserEnteredProductsData);
  }

  const selectSelectionSet = (
    newSelectionSet: ValueType<OptionsType, false>,
    triggeredAction: any,
  ) => {
    setSelectionSetParams({ newSelectionSet, triggeredAction });
    if (assets.length > 0) {
      setShowConfirmChangeSelectionSet(true);
    } else {
      handleSelectionSetSelection(newSelectionSet, triggeredAction);
    }
  };

  const handleSelectionSetSelection = async (
    newSelectionSet?: ValueType<OptionsType, false>,
    triggeredAction?: any,
  ) => {
    const action = (triggeredAction || selectionSetParams!.triggeredAction).action;
    const selectedSelectionSet = newSelectionSet || selectionSetParams!.newSelectionSet;
    if (action === 'clear') {
      setSelectionSet(undefined);
      setAssets([]);
    } else {
      setIsLoadingSelectionSet(true);
      setSelectionSet(selectedSelectionSet);
      if (selectedSelectionSet) {
        const payload = {
          assetSelectionSetId: selectedSelectionSet.value,
          firmNamePropertyDefinitionId:
            propertyDefinition && propertyDefinition.value > 0 ? propertyDefinition.value : null,
          productNamePropertyDefinitionId:
            propertyDefinition && propertyDefinition.value > 0 ? propertyDefinition.value : null,
          propertyDefinitionIds: propertyDefinition && propertyDefinition.value > 0 ? [propertyDefinition.value] : null,
        };
        let { assets: newAssets } = await mutate({
          url: ENDPOINTS.assetLookup(dataEnvironment!.id),
          payload,
          method: 'post',
        });
        newAssets = newAssets.filter(
          (asset: Asset) => !mappedAssetProductUploadIds?.includes(asset.productUploadAssetId),
        );
        const assetsForTable = newAssets.map((asset: Asset) => ({
          resourceId: asset.resourceId,
          name: asset.name,
          property:
            asset && asset.propertyValues.length > 0
              ? asset.propertyValues[0].value
              : null,
          productName: null,
          firmName: null,
          productId: null,
        }));
        setAssets([...assetsForTable]);
      }
      setIsLoadingSelectionSet(false);
      setSelectionSetParams(undefined);
    }
  };

  const handlePropertyDefinitionSelection = async (
    newPropertyDefinition: ValueType<OptionsType, false>,
    triggeredAction: any,
  ) => {
    if (triggeredAction.action === 'clear') {
      setPropertyDefinition(undefined);
      const assetsForTable: AssetForTable[] = assets.map((asset: AssetForTable) => {
        return {
          resourceId: asset.resourceId,
          name: asset.name,
          property: null,
          productName: asset.productName,
          firmName: asset.firmName,
          productId: asset.productId,
        };
      });
      setAssets([...assetsForTable]);
    } else {
      setIsLoadingPropertyDefinition(true);
      setPropertyDefinition(newPropertyDefinition);
      if (newPropertyDefinition) {
        const payload = {
          assetSelectionSetId: selectionSet && selectionSet.value > 0 ? selectionSet.value : null,
          firmNamePropertyDefinitionId: newPropertyDefinition.value,
          productNamePropertyDefinitionId: newPropertyDefinition.value,
          resourceIds: assets
            .filter((asset: AssetForTable) => asset.resourceId)
            .map((asset: AssetForTable) => asset.resourceId),
          propertyDefinitionIds: [newPropertyDefinition?.value],
        };
        let { assets: newAssets } = await mutate({
          url: ENDPOINTS.assetLookup(dataEnvironment!.id),
          payload,
          method: 'post',
        });
        newAssets = newAssets.filter(
          (asset: Asset) => !mappedAssetProductUploadIds?.includes(asset.productUploadAssetId),
        );
        const assetsForTable: AssetForTable[] = assets.map((asset: AssetForTable) => {
          const newAsset = newAssets.find(
            (newAsset: Asset) => newAsset.resourceId === asset.resourceId,
          );
          return {
            resourceId: asset.resourceId,
            name: asset.name,
            property:
              newAsset && newAsset.propertyValues.length > 0
                ? newAsset.propertyValues[0].value
                : null,
            productName: asset.productName,
            firmName: asset.firmName,
            productId: asset.productId,
          };
        });
        setAssets([...assetsForTable]);
      }
      setIsLoadingPropertyDefinition(false);
    }
  };

  const onSelectProductName = (resourceId: number, value: ValueType<OptionsType, false>) => {
    const unmappedUserEnteredProduct = unmappedUserEnteredProducts?.find(
      (product) => product.userEnteredProductId === value!.value,
    );
    const tempAssets = assets;
    const assetIndex = tempAssets.findIndex((asset) => asset.resourceId === resourceId);
    tempAssets[assetIndex]!.productName = value;
    tempAssets[assetIndex]!.firmName = unmappedUserEnteredProduct!.firmName;
    tempAssets[assetIndex]!.productId = unmappedUserEnteredProduct?.userEnteredProductId;
    setAssets([...tempAssets]);
  };

  const handleAddAsset = (asset: Asset) => {
    const newAsset = {
      resourceId: asset.resourceId,
      name: asset.name,
      property: null,
      productName: null,
      firmName: asset.firmName,
      productId: null,
    };

    const nonBlankAssets = assets.filter((assetRow) => assetRow.resourceId);
    setAssets([...nonBlankAssets, newAsset]);
    setShowAssetLookup(false);
  };

  const Cell = (props: any) => {
    const { column, row } = props;
    if (column.name === 'productName') {
      return (
        <td
          style={{
            fontSize: '80%',
            whiteSpace: 'normal',
            wordWrap: 'normal',
            overflow: 'visible',
            textOverflow: 'inherit',
            paddingTop: '0.15rem',
            paddingBottom: '0.15rem',
            paddingRight: '0.15rem',
            verticalAlign: 'middle',
          }}>
          <LookupCell
            onApply={onSelectProductName}
            value={row.productName}
            options={(unmappedUserEnteredProducts || []).map(
              ({ userEnteredProductId, productName }: UnmappedUserEnteredProductsResult) => ({
                value: userEnteredProductId,
                label: productName,
              }),
            )}
            resourceId={row.resourceId}
          />
        </td>
      );
    } else if (column.name === 'property' && isLoadingPropertyDefinition) {
      return (
        <td
          style={{
            fontSize: '80%',
            whiteSpace: 'normal',
            wordWrap: 'normal',
            overflow: 'visible',
            textOverflow: 'inherit',
            paddingTop: '0.15rem',
            paddingBottom: '0.15rem',
            paddingRight: '0.15rem',
            verticalAlign: 'middle',
          }}>
          <LoadingIndicator size="sm" />
        </td>
      );
    }
    return (
      <Table.Cell
        className=""
        {...props}
        style={{
          fontSize: '80%',
          whiteSpace: 'normal',
          wordWrap: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          paddingTop: '0.15rem',
          paddingBottom: '0.15rem',
          paddingRight: '0.15rem',
          verticalAlign: 'middle',
        }}
      />
    );
  };

  const ConfirmAssetRemoval: React.FC<{}> = () => (
    <ConfirmModal
      show={showConfirmAssetRemoval}
      onClose={() => {
        setShowConfirmRemoval(false);
      }}
      onConfirm={() => {
        const newAssets = assets.filter((asset) => asset.resourceId !== resourceIdToRemove);
        setAssets([...newAssets]);

        setShowConfirmRemoval(false);
      }}
      title="Asset Removal"
      message="Are you sure you want to remove this asset?"
    />
  );

  const ConfirmChangeSelectionSet: React.FC<{}> = () => (
    <ConfirmModal
      show={showConfirmChangeSelectionSet}
      onClose={() => {
        setShowConfirmChangeSelectionSet(false);
      }}
      onConfirm={() => {
        handleSelectionSetSelection();
        setShowConfirmChangeSelectionSet(false);
      }}
      title="Change Selection Set"
      message="Changing the selection set will clear the assets in the table. Are you sure you wish to proceed?"
    />
  );

  const ConfirmClear: React.FC<{}> = () => (
    <ConfirmModal
      show={showConfirmClear}
      onClose={() => {
        setShowConfirmClear(false);
      }}
      onConfirm={() => {
        clear();
        setShowConfirmClear(false);
      }}
      title="Clear Form"
      message="This will clear the assets in the table. Are you sure you wish to proceed?"
    />
  );

  const handleRemoveRow = (data: AssetForTable) => {
    setResourceIdToRemove(data.resourceId);
    setShowConfirmRemoval(true);
  };

  const clear = () => {
    setSelectionSet(null);
    setPropertyDefinition(null);
    setAssets([]);
  };

  const handleSaveMappings = async () => {
    const payload = {
      mappings: assets
        .filter((asset) => asset.resourceId)
        .map((asset) => ({
          resourceId: asset.resourceId,
          userEnteredProductId: asset.productName ? asset.productName.value : null,
        })),
    };

    const hasDuplicateProducts =
      new Set(assets.map((asset) => asset.productId)).size !== assets.length;

    if (hasDuplicateProducts) {
      showError('Save failed. Duplicate products detected in table.');
    } else {
      try {
        await mutate({
          url: ENDPOINTS.mapAssets(dataEnvironment!.id),
          payload,
          method: 'post',
        });
        clear();
        showSuccess('The mappings have been added.');
        history.push(routes.getMappedAssetsRoute());
      } catch (error) {
        showError(errorMessage({ error, defaultMessage: 'An error occurred mapping the assets.' }));
      }
    }
  };

  const ActionsFormatter: React.FC<DataTypeProvider.ValueFormatterProps> = ({
    row: data,
  }: DataTypeProvider.ValueFormatterProps) => {
    return data.resourceId ? (
      <>
        <button className="btn btn-link p-0" onClick={() => handleRemoveRow(data as AssetForTable)}>
          Remove
        </button>
      </>
    ) : (
      <></>
    );
  };

  const ActionsTypeProvider: React.FC<DataTypeProviderProps> = (props: DataTypeProviderProps) => (
    <DataTypeProvider formatterComponent={ActionsFormatter} {...props} />
  );

  const canSave = (): boolean => {
    return (
      assets.length > 0 && assets.filter((asset) => asset.productName).length === assets.length
    );
  };

  const canClear = (): boolean => {
    return assets.length > 0;
  };

  const [sortingStateColumnExtensions] = useState([
    { columnName: 'actions', sortingEnabled: false },
  ]);

  const integratedSortingColumnExtensions = [
    { columnName: 'productName', compare: sortProductName },
  ];

  return (
    <Fragment>
      <UploadModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
      <RouteGuard
        blockLocation={(nextLocation) => nextLocation.pathname !== location.pathname}
        message={'There are unmapped assets in the grid, any changes will be lost.'}
        navigate={history.push}
        when={assets.length > 0 || !!selectionSet || !!propertyDefinition}
      />
      <div style={{ paddingBottom: 100 }}>
        <ConfirmAssetRemoval />
        <ConfirmChangeSelectionSet />
        <ConfirmClear />
        <PoweredByHeader />
        <Tabs />
        <div className="row">
          <div className="col-3">
            <small>
              <label htmlFor="selectionSet">Selection Set</label>
            </small>
            <SelectionSetSelector
              onChange={selectSelectionSet}
              selectionSets={selectionSets || []}
              selectionSet={selectionSet}
              loading={isFetchingSelectionSets}
            />
          </div>
          <div className="col-3">
            <small>
              <label htmlFor="propertyDefinition">Property</label>
            </small>
            <PropertyDefinitionSelector
              onChange={handlePropertyDefinitionSelection}
              propertyDefinitions={propertyDefinitions || []}
              propertyDefinition={propertyDefinition}
              loading={isFetchingPropertyDefinitions}
            />
          </div>
          <div
            className="col-6 text-right pt-4"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
            <span style={{ display: 'inline-block', alignSelf: 'flex-end' }}>
              <Button
                onClick={() => {
                  setShowUploadModal(true);
                }}
                className="mr-2"
                variant="semitransparent">
                Upload Mappings
              </Button>
              <Button
                onClick={() => {
                  setShowAssetLookup(true);
                  setHideNoDataMessage(true);
                  window.scrollTo({
                    top: document.getElementById('asset-lookup')!.offsetTop,
                    behavior: 'smooth',
                  });
                }}
                variant="semitransparent">
                Add Asset
              </Button>
            </span>
          </div>
        </div>
        <div className="mt-3">
          {isLoadingSelectionSet ? (
            <div className="text-center p-5">
              <LoadingIndicator />
            </div>
          ) : (
            <Grid rows={assets} columns={columns}>
              <SortingState
                defaultSorting={[{ columnName: 'name', direction: 'asc' }]}
                columnExtensions={sortingStateColumnExtensions}
              />
              <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
              <Table
                cellComponent={Cell}
                noDataCellComponent={(props: PropsWithChildren<Table.NoDataCellProps>) =>
                  hideNoDataMessage ? null : (
                    <td className="p-5 text-center" colSpan={props.colSpan}>
                      No Data
                    </td>
                  )
                }
              />
              <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
              <ActionsTypeProvider for={['actions']} />
              <TableHeaderRow showSortingControls />
            </Grid>
          )}
        </div>
        <div
          style={{
            width: '25%',
            float: 'left',
          }}>
          <span id="asset-lookup" />
          <AssetLookup
            onAddAsset={handleAddAsset}
            productUploadAssetIdsToExclude={mappedAssetProductUploadIds || []}
            resourceIdsToExclude={assets
              .filter((asset: AssetForTable) => asset.resourceId)
              .map((asset: AssetForTable) => asset.resourceId)}
            show={showAssetLookup}
          />
        </div>
        <div className="text-right fixed-bottom p-3" style={{ backgroundColor: '#E8EDF2' }}>
          <Button
            className={'mr-2'}
            variant={'semitransparent'}
            onClick={() => setShowConfirmClear(true)}
            disabled={!canClear()}
          >
            Clear
          </Button>
          <Button variant={'primary'} onClick={handleSaveMappings} disabled={!canSave()}>
            Save Mappings
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

export default AssetMappings;
